import { useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownToLine, FileText } from 'lucide-react';
import { recordingPlaybackUrl, recordingDownloadUrl } from '@/services/dialer/recordingUrl';
import { buildRecordingFilename } from '@/services/dialer/callHistory';

/**
 * The call recording player (#34), shared by Historique and Objections (W4).
 *
 * Replaces the native <audio controls>, whose progress bar is neither
 * colourable nor measurable (closed shadow DOM): here the track and the fill
 * carry explicit colours, so « the progress bar is visible » is checkable.
 * The fill is a dark violet, not --vf-accent: the variable resolves to a
 * light violet that only gave 2.85:1 against the track, under the 3:1 of UI
 * components. Measured: 4.8:1.
 */
export interface RecordedCall {
  recording_url?: string | null;
  transcription?: string | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
  created_date?: string | null;
  started_at?: string | null;
  connected_at?: string | null;
}

const mmss = (s: number) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

export function RecordingPlayer({ src, compact = false, startAt }: { src: string; compact?: boolean; startAt?: number }) {
  const { t } = useTranslation(['dialer']);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);

  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  };

  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;

  return (
    <div
      className={`flex items-center gap-2.5 rounded-md border border-border px-3 py-2 ${compact ? 'w-[260px]' : 'w-[560px]'}`}
      style={{ background: 'var(--vf-surface-2, rgb(241 245 249))' }}
      data-testid="lecteur-enregistrement"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? t('dialer:callHistory.player.pause') : t('dialer:callHistory.player.play')}
        data-testid="lecteur-bouton"
        className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full text-white"
        style={{ background: 'var(--vf-accent, #7c3aed)' }}
      >
        {playing
          ? <span style={{ display: 'inline-flex', gap: 2 }}>
              <span style={{ width: 3, height: 11, background: '#fff', borderRadius: 1 }} />
              <span style={{ width: 3, height: 11, background: '#fff', borderRadius: 1 }} />
            </span>
          : <span style={{ width: 0, height: 0, borderLeft: '9px solid #fff', borderTop: '6px solid transparent', borderBottom: '6px solid transparent', marginLeft: 2 }} />}
      </button>

      <span className="text-[11px] font-mono text-muted-foreground tabular-nums shrink-0 w-9">{mmss(position)}</span>

      <div
        data-testid="lecteur-piste"
        className="relative flex-1 h-2 rounded-full cursor-pointer overflow-hidden"
        style={{ background: '#cbd5e1', boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.18)' }}
        onClick={(e) => {
          e.stopPropagation();
          const a = audioRef.current;
          if (!a || !duration) return;
          const r = e.currentTarget.getBoundingClientRect();
          a.currentTime = ((e.clientX - r.left) / r.width) * duration;
        }}
      >
        <div
          data-testid="lecteur-progression"
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ width: `${pct}%`, background: '#6d28d9' }}
        />
      </div>

      <span className="text-[11px] font-mono text-muted-foreground tabular-nums shrink-0 w-9 text-right">
        {ready ? mmss(duration) : '--:--'}
      </span>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration || 0;
          setDuration(d);
          setReady(true);
          // Objections: start where the objection was heard.
          if (startAt && startAt > 0 && startAt < d) { e.currentTarget.currentTime = startAt; setPosition(startAt); }
        }}
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setPosition(0); }}
        className="hidden"
      />
    </div>
  );
}

export function downloadTranscriptTxt(call: RecordedCall, t: (k: string) => string) {
  const transcription = call.transcription;
  if (!transcription) return;
  let text = '';
  try {
    const parsed = typeof transcription === 'string' ? JSON.parse(transcription) : transcription;
    if (parsed?.segments && Array.isArray(parsed.segments)) {
      const agentLabel = t('dialer:callHistory.detail.agent');
      const prospectLabel = t('common:entity.prospect');
      text = parsed.segments.map((seg: { speaker?: string; timestamp?: string; text?: string }) => {
        const speaker = seg.speaker === 'outbound' ? agentLabel : seg.speaker === 'inbound' ? prospectLabel : (seg.speaker || '?');
        const ts = seg.timestamp ? `[${seg.timestamp}] ` : '';
        return `${ts}${speaker}: ${seg.text}`;
      }).join('\n');
    } else {
      text = String(transcription);
    }
  } catch {
    text = String(transcription);
  }
  const parts = [call.prospect_name, call.prospect_company].filter(Boolean);
  const filename = (parts.length > 0 ? parts.join(' - ').replace(/[/\\?%*:|"<>]/g, '_') : t('dialer:callHistory.detail.transcription')) + '.txt';
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Player, download and transcript, or « — » when the call has neither. */
export function RecordingCell({ call, compact = false, startAt }: { call: RecordedCall; compact?: boolean; startAt?: number }) {
  const { t } = useTranslation(['dialer', 'pages', 'common']);
  const hasRecording = !!call.recording_url;
  const hasTranscript = !!call.transcription;
  if (!hasRecording && !hasTranscript) return <span className="text-muted-foreground/40 text-xs">—</span>;

  const filename = buildRecordingFilename(call, t);
  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'min-w-[620px]'}`}>
      {hasRecording && (
        <>
          <RecordingPlayer src={recordingPlaybackUrl(call.recording_url as string)} compact={compact} startAt={startAt} />
          <a
            href={recordingDownloadUrl(call.recording_url as string, filename)}
            download={filename}
            title={t('pages:callHistory.downloadRecording')}
            aria-label={t('pages:callHistory.downloadRecording')}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowDownToLine className="w-3.5 h-3.5" aria-hidden />
          </a>
        </>
      )}
      {hasTranscript && (
        <button
          type="button"
          title={t('dialer:callHistory.downloadTranscript')}
          aria-label={t('dialer:callHistory.downloadTranscript')}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          onClick={(e) => { e.stopPropagation(); downloadTranscriptTxt(call, t); }}
        >
          <FileText className="w-3.5 h-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}
